/**
 * Pure helpers for the chat assistant's segmented message mode. A segmented
 * assistant message is an ordered list of text and tool segments, so tool
 * activity renders inline WITHOUT discarding the narration streamed before a
 * tool call (the copilot needs "narrate while building"; the legacy
 * ChatAssistant mode clears narration on tool_start and is unchanged).
 */

export type MessageSegment =
  | { type: 'text'; text: string }
  | {
      type: 'tool'
      toolName: string
      toolCallId: string
      status: 'started' | 'executing' | 'done'
      success?: boolean
      summary?: string
    }

export const appendTextToSegments = (
  segments: MessageSegment[],
  text: string
): MessageSegment[] => {
  const last = segments[segments.length - 1]
  if (last && last.type === 'text') {
    return [...segments.slice(0, -1), { type: 'text', text: last.text + text }]
  }
  return [...segments, { type: 'text', text }]
}

export const startToolSegment = (
  segments: MessageSegment[],
  toolName: string,
  toolCallId: string
): MessageSegment[] => [...segments, { type: 'tool', toolName, toolCallId, status: 'started' }]

export const updateToolSegment = (
  segments: MessageSegment[],
  toolCallId: string,
  update: Partial<Extract<MessageSegment, { type: 'tool' }>>
): MessageSegment[] =>
  segments.map((segment) =>
    segment.type === 'tool' && segment.toolCallId === toolCallId
      ? { ...segment, ...update }
      : segment
  )

/** Concatenated text content of a segmented message (parity with plain content). */
export const segmentsToText = (segments: MessageSegment[]): string =>
  segments
    .filter((s): s is Extract<MessageSegment, { type: 'text' }> => s.type === 'text')
    .map((s) => s.text)
    .join('')
